import type { ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  className?: string;
  sortable?: boolean;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Resalta la fila cuyo resultado sea `true` (ej. la orden abierta en un master-detail). */
  isRowSelected?: (row: T) => boolean;
  /** Se muestra en lugar del cuerpo de la tabla cuando `rows` está vacío. */
  emptyState?: ReactNode;
  sortKey?: string;
  sortDir?: "asc" | "desc";
  onSortChange?: (key: string) => void;
  className?: string;
  /** SCRUM-232: filas densas (padding reducido). */
  density?: "default" | "compact";
}

/**
 * DataTable canónico del Design System (F5) — reemplaza las tablas `<table>` reimplementadas
 * en cada ruta de Catálogo (Productos, Categorías, Combos, Recetas, ajuste de precios).
 * Puramente presentacional: no pagina ni ordena datos por sí mismo, solo renderiza filas
 * y expone el header de columna clickeable cuando `onSortChange` está presente.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  isRowSelected,
  emptyState,
  sortKey,
  sortDir,
  onSortChange,
  className,
  density = "default",
}: DataTableProps<T>) {
  const cellPad = density === "compact" ? "px-3 py-1.5" : "px-4 py-3";
  return (
    <div className={cn("overflow-hidden rounded-xl border border-border", className)}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/30 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <tr>
              {columns.map((col) => {
                const isSorted = sortKey === col.key;
                const canSort = col.sortable && onSortChange;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    className={cn(cellPad, col.className, canSort && "cursor-pointer select-none")}
                    onClick={canSort ? () => onSortChange(col.key) : undefined}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.header}
                      {canSort && isSorted ? (
                        sortDir === "desc" ? (
                          <ChevronDown className="h-3 w-3" aria-hidden />
                        ) : (
                          <ChevronUp className="h-3 w-3" aria-hidden />
                        )
                      ) : null}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="p-0">
                  {emptyState}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={getRowKey(row)}
                  onClick={
                    onRowClick
                      ? (e) => {
                          // Un botón de acción (editar, eliminar, switch) dentro de la fila no
                          // debe además disparar la navegación/edición de la fila completa —
                          // sin este guard, el click en "Eliminar" abre a la vez el modal de
                          // confirmación Y la vista de edición de la fila.
                          if ((e.target as HTMLElement).closest("button, a, input, select, [role=\"button\"]")) return;
                          onRowClick(row);
                        }
                      : undefined
                  }
                  className={cn(
                    "bg-card transition-colors hover:bg-surface-hover",
                    onRowClick && "cursor-pointer",
                    isRowSelected?.(row) && "bg-primary/5 hover:bg-primary/5",
                  )}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={cn(cellPad, "align-middle", col.className)}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
